import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, UserPlus, Activity, FileText, Clock, Star, Eye, BookOpenCheck,
  ShieldCheck, Search, Lock, AlertCircle, UserX, Mail, ChevronLeft, ChevronRight, HardDrive,
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuthContext';
import StatTile from '../components/charts/StatTile';
import ChartCard from '../components/charts/ChartCard';
import { TimeColumns, Donut } from '../components/charts/Charts';
import { formatMinutes, formatCompact } from '../components/charts/chartTheme';

const RANGES = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
];

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'people', label: 'People' },
  { id: 'content', label: 'Content' },
  { id: 'reports', label: 'Reports' },
];

const REPORT_STATUSES = ['pending', 'in-progress', 'resolved', 'closed'];

const formatBytes = (bytes) => {
  const b = Number(bytes) || 0;
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
  if (b >= 1024 ** 2) return `${Math.round(b / 1024 ** 2)} MB`;
  if (b >= 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
};

const relative = (value) => {
  if (!value) return 'never';
  const diff = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(value).toLocaleDateString('en-GB');
};

/* ── Sign-in, shown once the slug is confirmed ────────────────────────────── */
const AdminLogin = ({ onSignedIn, notAdmin }) => {
  const { dispatch } = useAuthContext();
  const [form, setForm] = useState({ email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(notAdmin ? 'That account is not an administrator.' : null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Sign in failed.');
      if (data.user?.role !== 'admin') throw new Error('That account is not an administrator.');
      localStorage.setItem('user', JSON.stringify({ ...data.user, token: data.token }));
      dispatch({ type: 'LOGIN', payload: { ...data.user, token: data.token } });
      onSignedIn();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-xl p-8">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <h1 className="text-lg font-bold text-foreground">Administrator sign in</h1>
        <p className="text-xs text-muted-foreground mt-1 mb-6">This area is restricted.</p>

        <label htmlFor="admin-email" className="text-xs font-bold text-foreground mb-1.5 block">Email</label>
        <input
          id="admin-email"
          type="email"
          autoComplete="username"
          required
          value={form.email}
          onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
          className="w-full px-3 py-2 rounded-lg bg-muted border-0 text-sm text-foreground mb-4 focus:outline-none focus:ring-2 focus:ring-ring"
        />

        <label htmlFor="admin-password" className="text-xs font-bold text-foreground mb-1.5 block">Password</label>
        <input
          id="admin-password"
          type="password"
          autoComplete="current-password"
          required
          value={form.password}
          onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
          className="w-full px-3 py-2 rounded-lg bg-muted border-0 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />

        {error && (
          <p className="flex items-start gap-2 text-xs text-destructive mt-4">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full mt-6 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-50 border-none cursor-pointer transition-colors"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="text-[11px] text-muted-foreground mt-4 text-center">
          Forgot it? Reset by email from the normal sign-in page.
        </p>
      </form>
    </div>
  );
};

/* ── The panel ────────────────────────────────────────────────────────────── */
const AdminPanel = ({ slug }) => {
  const { user, token } = useAuthContext();

  const [gate, setGate] = useState('checking'); // checking | open | denied
  const [tab, setTab] = useState('overview');
  const [range, setRange] = useState('30');

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [people, setPeople] = useState(null);
  const [peopleQuery, setPeopleQuery] = useState({ search: '', page: 1, sort: 'recent' });
  const [searchDraft, setSearchDraft] = useState('');

  const [content, setContent] = useState(null);
  const [contentPage, setContentPage] = useState(1);

  const [reports, setReports] = useState(null);
  const [reportFilter, setReportFilter] = useState('all');

  const isAdmin = user?.role === 'admin';

  // The slug is never shipped in the bundle — the server is the only thing
  // that knows it, and it answers nothing but yes or no.
  useEffect(() => {
    let alive = true;
    fetch(`/api/admin/gate/${encodeURIComponent(slug)}`)
      .then((res) => { if (alive) setGate(res.ok ? 'open' : 'denied'); })
      .catch(() => { if (alive) setGate('denied'); });
    return () => { alive = false; };
  }, [slug]);

  const authed = useCallback((path) => fetch(path, { headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    if (gate !== 'open' || !isAdmin) return;
    setLoading(true);
    authed(`/api/admin/overview?range=${range}`)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Could not load the overview.'))))
      .then(setOverview)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [gate, isAdmin, range, authed]);

  useEffect(() => {
    if (gate !== 'open' || !isAdmin || tab !== 'people') return;
    const q = new URLSearchParams({ page: peopleQuery.page, search: peopleQuery.search, sort: peopleQuery.sort });
    authed(`/api/admin/users?${q}`).then((r) => (r.ok ? r.json() : null)).then(setPeople).catch(() => {});
  }, [gate, isAdmin, tab, peopleQuery, authed]);

  useEffect(() => {
    if (gate !== 'open' || !isAdmin || tab !== 'content') return;
    authed(`/api/admin/content?page=${contentPage}`).then((r) => (r.ok ? r.json() : null)).then(setContent).catch(() => {});
  }, [gate, isAdmin, tab, contentPage, authed]);

  useEffect(() => {
    if (gate !== 'open' || !isAdmin || tab !== 'reports') return;
    authed(`/api/admin/reports?status=${reportFilter}`).then((r) => (r.ok ? r.json() : null)).then(setReports).catch(() => {});
  }, [gate, isAdmin, tab, reportFilter, authed]);

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setPeopleQuery((p) => ({ ...p, search: searchDraft, page: 1 })), 350);
    return () => clearTimeout(id);
  }, [searchDraft]);

  const setReportStatus = async (id, status) => {
    const res = await fetch(`/api/admin/reports/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      setReports((prev) => ({
        ...prev,
        reports: prev.reports.map((r) => (r.id === id ? { ...r, status } : r)),
      }));
    }
  };

  const retention = useMemo(() => {
    if (!overview) return null;
    const total = overview.people.total || 0;
    const returned = total - (overview.people.neverReturned || 0);
    return { returned, percent: total ? Math.round((returned / total) * 100) : 0 };
  }, [overview]);

  // An unknown slug must be indistinguishable from any other dead URL.
  if (gate === 'checking') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
      </div>
    );
  }

  if (gate === 'denied') {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-3 px-6 text-center">
        <Lock className="w-8 h-8 text-muted-foreground" />
        <h1 className="text-lg font-bold text-foreground">404 — page not found</h1>
        <p className="text-sm text-muted-foreground">The page you are looking for does not exist.</p>
      </div>
    );
  }

  if (!token || !isAdmin) {
    return <AdminLogin notAdmin={Boolean(token) && !isAdmin} onSignedIn={() => setTab('overview')} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-nav-bg border-b border-border">
        <div className="max-w-[1440px] mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground leading-none">YeniDers admin</p>
              <p className="text-[10px] text-muted-foreground mt-1">{user?.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-1 bg-card border border-border/60 rounded-xl p-1" role="group" aria-label="Time range">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                aria-pressed={range === r.value}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors border-none cursor-pointer ${
                  range === r.value ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        <nav className="max-w-[1440px] mx-auto px-6 flex items-center gap-1" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`relative px-4 py-2.5 text-sm bg-transparent border-none cursor-pointer transition-colors ${
                tab === t.id ? 'text-foreground font-bold' : 'text-muted-foreground hover:text-foreground font-medium'
              }`}
            >
              {t.label}
              {t.id === 'reports' && overview?.ops.openReports > 0 && (
                <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-destructive/15 text-destructive">
                  {overview.ops.openReports}
                </span>
              )}
              <span
                aria-hidden="true"
                className={`absolute bottom-0 left-3 right-3 h-[2px] rounded-full bg-primary origin-center transition-transform duration-300 ${
                  tab === t.id ? 'scale-x-100' : 'scale-x-0'
                }`}
              />
            </button>
          ))}
        </nav>
      </header>

      <main className={`max-w-[1440px] mx-auto px-6 py-6 transition-opacity ${loading ? 'opacity-60' : 'opacity-100'}`}>
        {error && (
          <div className="p-4 rounded-xl bg-destructive/10 text-destructive text-sm font-medium mb-4">{error}</div>
        )}

        {/* ── Overview ─────────────────────────────────────────────────── */}
        {tab === 'overview' && overview && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatTile icon={Users} label="Total members" value={formatCompact(overview.people.total)} hint="all time" />
              <StatTile icon={UserPlus} label="New members" value={formatCompact(overview.people.new.value)} change={overview.people.new.change} />
              <StatTile icon={Activity} label="Active members" value={formatCompact(overview.people.active.value)} change={overview.people.active.change} />
              <StatTile icon={FileText} label="Materials" value={formatCompact(overview.content.total)} hint={`+${overview.content.new.value} this period`} />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatTile icon={Activity} label="Active today" value={formatCompact(overview.people.activeToday)} hint="signed in and using it" />
              <StatTile icon={Activity} label="Active this week" value={formatCompact(overview.people.activeWeek)} hint="last 7 days" />
              <StatTile icon={UserX} label="Never came back" value={formatCompact(overview.people.neverReturned)} hint="signed up, never logged in" />
              <StatTile icon={Mail} label="Newsletter" value={formatCompact(overview.ops.newsletterCount)} hint="subscribers" />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatTile icon={HardDrive} label="Storage used" value={formatBytes(overview.content.storageBytes)} hint="all uploaded files" />
              <StatTile icon={FileText} label="New materials" value={formatCompact(overview.content.new.value)} change={overview.content.new.change} />
              <StatTile icon={Clock} label="Study time" value={formatMinutes(overview.engagement.studyMinutes)} hint="across everyone" />
              <StatTile icon={AlertCircle} label="Open reports" value={formatCompact(overview.ops.openReports)} hint="need a look" />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <ChartCard
                title="New members"
                subtitle={`${overview.people.new.value} joined in this period`}
                empty={!overview.people.signupSeries.some((d) => d.value) ? { title: 'No signups in this period', hint: 'Try a longer range.' } : null}
                table={{ head: ['Date', 'Signups'], rows: overview.people.signupSeries.map((d) => [d.label, d.value]) }}
              >
                <TimeColumns data={overview.people.signupSeries} valueLabel="Signups" />
              </ChartCard>

              <ChartCard
                title="Members active"
                subtitle={`${overview.people.active.value} used the site · ${retention?.percent}% of all members have returned at least once`}
                empty={!overview.people.activeSeries.some((d) => d.value) ? { title: 'No activity recorded', hint: 'Activity is tracked from the next sign-in onwards.' } : null}
                table={{ head: ['Date', 'Active'], rows: overview.people.activeSeries.map((d) => [d.label, d.value]) }}
              >
                <TimeColumns data={overview.people.activeSeries} valueLabel="Active" />
              </ChartCard>

              <ChartCard
                title="Uploads"
                subtitle={`${overview.content.new.value} new materials`}
                empty={!overview.content.uploadSeries.some((d) => d.value) ? { title: 'No uploads in this period', hint: 'Try a longer range.' } : null}
                table={{ head: ['Date', 'Uploads'], rows: overview.content.uploadSeries.map((d) => [d.label, d.value]) }}
              >
                <TimeColumns data={overview.content.uploadSeries} valueLabel="Uploads" />
              </ChartCard>

              <ChartCard
                title="Study time across the site"
                subtitle={formatMinutes(overview.engagement.studyMinutes)}
                empty={!overview.engagement.studySeries.some((d) => d.value) ? { title: 'No study time recorded', hint: 'Time is counted while a material is open.' } : null}
                table={{ head: ['Date', 'Studied'], rows: overview.engagement.studySeries.map((d) => [d.label, formatMinutes(d.value)]) }}
              >
                <TimeColumns data={overview.engagement.studySeries} valueLabel="Studied" format={formatMinutes} />
              </ChartCard>

              <ChartCard
                title="Members by role"
                subtitle="What people said they are"
                empty={!overview.people.byStatus.length ? { title: 'No members yet', hint: '' } : null}
                table={{ head: ['Role', 'People'], rows: overview.people.byStatus.map((d) => [d.name, d.value]) }}
              >
                <Donut data={overview.people.byStatus} totalLabel="members" />
              </ChartCard>

              <ChartCard
                title="Materials by type"
                subtitle="What gets uploaded"
                empty={!overview.content.byCategory.length ? { title: 'Nothing uploaded yet', hint: '' } : null}
                table={{ head: ['Type', 'Materials'], rows: overview.content.byCategory.map((d) => [d.name, d.value]) }}
              >
                <Donut data={overview.content.byCategory} totalLabel="materials" />
              </ChartCard>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <StatTile icon={BookOpenCheck} label="Material reads" value={formatCompact(overview.engagement.materialReads)} hint="in this period" />
              <StatTile icon={Eye} label="Profile visits" value={formatCompact(overview.engagement.profileVisits)} hint="in this period" />
              <StatTile icon={Star} label="Ratings given" value={formatCompact(overview.engagement.ratingsGiven)} hint="in this period" />
              <StatTile icon={Users} label="Follows made" value={formatCompact(overview.engagement.followsMade)} hint="in this period" />
              <StatTile icon={Clock} label="Quizzes taken" value={formatCompact(overview.engagement.quizzesTaken)} hint="in this period" />
            </div>
          </div>
        )}

        {/* ── People ───────────────────────────────────────────────────── */}
        {tab === 'people' && (
          <section className="bg-card rounded-2xl border border-border/40 shadow-sm overflow-hidden">
            <header className="flex items-center justify-between gap-3 p-5 flex-wrap">
              <div>
                <h2 className="text-sm font-bold text-foreground">Members</h2>
                <p className="text-xs text-muted-foreground mt-0.5">{people?.total ?? '—'} accounts</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
                  <input
                    value={searchDraft}
                    onChange={(e) => setSearchDraft(e.target.value)}
                    placeholder="Name, username or email"
                    aria-label="Search members"
                    className="pl-9 pr-3 py-2 rounded-lg bg-muted border-0 text-xs text-foreground w-60 focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <select
                  value={peopleQuery.sort}
                  onChange={(e) => setPeopleQuery((p) => ({ ...p, sort: e.target.value, page: 1 }))}
                  aria-label="Sort members"
                  className="bg-muted border-0 rounded-lg px-2 py-2 text-xs font-bold text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="recent">Newest</option>
                  <option value="active">Last active</option>
                  <option value="points">Points</option>
                  <option value="name">Name</option>
                </select>
              </div>
            </header>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-y border-border/40 bg-muted/30">
                    <th scope="col" className="text-left font-bold text-muted-foreground px-5 py-2.5">Member</th>
                    <th scope="col" className="text-left font-bold text-muted-foreground px-3 py-2.5">Joined</th>
                    <th scope="col" className="text-left font-bold text-muted-foreground px-3 py-2.5">Last active</th>
                    <th scope="col" className="text-left font-bold text-muted-foreground px-3 py-2.5">Last login</th>
                    <th scope="col" className="text-right font-bold text-muted-foreground px-3 py-2.5">Logins</th>
                    <th scope="col" className="text-right font-bold text-muted-foreground px-3 py-2.5">Materials</th>
                    <th scope="col" className="text-right font-bold text-muted-foreground px-5 py-2.5">Points</th>
                  </tr>
                </thead>
                <tbody>
                  {(people?.users || []).map((u) => (
                    <tr key={u.id} className="border-b border-border/30 last:border-0 hover:bg-muted/40 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {u.avatar_url
                              ? <img src={u.avatar_url} alt="" className="w-full h-full object-cover" />
                              : <span className="text-[10px] font-black">{(u.firstname?.[0] || '?').toUpperCase()}</span>}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-foreground truncate flex items-center gap-1.5">
                              {u.firstname} {u.lastname}
                              {u.role === 'admin' && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-primary/15 text-primary uppercase">admin</span>
                              )}
                            </p>
                            <p className="text-[10px] text-muted-foreground truncate">@{u.username} · {u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{new Date(u.createdAt).toLocaleDateString('en-GB')}</td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className={u.last_active_at ? 'text-foreground font-medium' : 'text-muted-foreground'}>
                          {relative(u.last_active_at)}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{relative(u.last_login_at)}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">{u.login_count || 0}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-bold text-foreground">{u.materialsCount}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">{formatCompact(u.points || 0)}</td>
                    </tr>
                  ))}
                  {people && people.users.length === 0 && (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-muted-foreground">No members match that search.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {people && people.totalPages > 1 && (
              <footer className="flex items-center justify-between gap-3 p-4 border-t border-border/40">
                <span className="text-[11px] text-muted-foreground">Page {people.page} of {people.totalPages}</span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPeopleQuery((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                    disabled={people.page <= 1}
                    aria-label="Previous page"
                    className="w-8 h-8 rounded-lg bg-muted hover:bg-muted/70 flex items-center justify-center border-none cursor-pointer disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPeopleQuery((p) => ({ ...p, page: Math.min(people.totalPages, p.page + 1) }))}
                    disabled={people.page >= people.totalPages}
                    aria-label="Next page"
                    className="w-8 h-8 rounded-lg bg-muted hover:bg-muted/70 flex items-center justify-center border-none cursor-pointer disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </footer>
            )}
          </section>
        )}

        {/* ── Content ──────────────────────────────────────────────────── */}
        {tab === 'content' && (
          <section className="bg-card rounded-2xl border border-border/40 shadow-sm overflow-hidden">
            <header className="p-5">
              <h2 className="text-sm font-bold text-foreground">Materials</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{content?.total ?? '—'} uploaded across all accounts</p>
            </header>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-y border-border/40 bg-muted/30">
                    <th scope="col" className="text-left font-bold text-muted-foreground px-5 py-2.5">Material</th>
                    <th scope="col" className="text-left font-bold text-muted-foreground px-3 py-2.5">Owner</th>
                    <th scope="col" className="text-left font-bold text-muted-foreground px-3 py-2.5">Uploaded</th>
                    <th scope="col" className="text-right font-bold text-muted-foreground px-3 py-2.5">Views</th>
                    <th scope="col" className="text-right font-bold text-muted-foreground px-5 py-2.5">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {(content?.materials || []).map((m) => (
                    <tr key={m.id} className="border-b border-border/30 last:border-0 hover:bg-muted/40 transition-colors">
                      <td className="px-5 py-3">
                        <p className="font-bold text-foreground truncate max-w-[280px]">{m.title}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          {m.category}{m.visibility !== 'Public' && ` · ${m.visibility}`}
                        </p>
                      </td>
                      <td className="px-3 py-3 text-muted-foreground truncate max-w-[160px]">{m.owner ? `@${m.owner.username}` : '—'}</td>
                      <td className="px-3 py-3 text-muted-foreground whitespace-nowrap">{new Date(m.createdAt).toLocaleDateString('en-GB')}</td>
                      <td className="px-3 py-3 text-right tabular-nums font-bold text-foreground">{formatCompact(m.views || 0)}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-muted-foreground">{m.rating ? Number(m.rating).toFixed(1) : '—'}</td>
                    </tr>
                  ))}
                  {content && content.materials.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-10 text-center text-muted-foreground">Nothing uploaded yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {content && content.totalPages > 1 && (
              <footer className="flex items-center justify-between gap-3 p-4 border-t border-border/40">
                <span className="text-[11px] text-muted-foreground">Page {content.page} of {content.totalPages}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setContentPage((p) => Math.max(1, p - 1))} disabled={content.page <= 1} aria-label="Previous page"
                          className="w-8 h-8 rounded-lg bg-muted hover:bg-muted/70 flex items-center justify-center border-none cursor-pointer disabled:opacity-40 transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button onClick={() => setContentPage((p) => Math.min(content.totalPages, p + 1))} disabled={content.page >= content.totalPages} aria-label="Next page"
                          className="w-8 h-8 rounded-lg bg-muted hover:bg-muted/70 flex items-center justify-center border-none cursor-pointer disabled:opacity-40 transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </footer>
            )}
          </section>
        )}

        {/* ── Reports ──────────────────────────────────────────────────── */}
        {tab === 'reports' && (
          <section className="space-y-3">
            <div className="flex items-center gap-1 bg-card border border-border/60 rounded-xl p-1 w-fit" role="group" aria-label="Filter reports">
              {['all', ...REPORT_STATUSES].map((s) => (
                <button
                  key={s}
                  onClick={() => setReportFilter(s)}
                  aria-pressed={reportFilter === s}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize transition-colors border-none cursor-pointer ${
                    reportFilter === s ? 'bg-primary text-primary-foreground' : 'bg-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {s.replace('-', ' ')}
                </button>
              ))}
            </div>

            {(reports?.reports || []).map((r) => (
              <article key={r.id} className="bg-card rounded-2xl border border-border/40 p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground uppercase">{r.type}</span>
                      <span className="text-[11px] text-muted-foreground">
                        {r.reporter ? `@${r.reporter.username}` : 'anonymous'} · {new Date(r.createdAt).toLocaleDateString('en-GB')}
                      </span>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-line max-w-2xl">{r.description}</p>
                  </div>

                  <select
                    value={r.status}
                    onChange={(e) => setReportStatus(r.id, e.target.value)}
                    aria-label={`Status for report from ${r.reporter?.username || 'anonymous'}`}
                    className="bg-muted border-0 rounded-lg px-2 py-1.5 text-xs font-bold text-foreground cursor-pointer capitalize focus:outline-none focus:ring-2 focus:ring-ring flex-shrink-0"
                  >
                    {REPORT_STATUSES.map((s) => <option key={s} value={s}>{s.replace('-', ' ')}</option>)}
                  </select>
                </div>
              </article>
            ))}

            {reports && reports.reports.length === 0 && (
              <div className="bg-card rounded-2xl border border-border/40 p-10 text-center">
                <p className="text-sm font-semibold text-foreground">Nothing here</p>
                <p className="text-xs text-muted-foreground mt-1">No reports with that status.</p>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
